import { Variant } from '@kizi/core-components';
import anchors from './anchors';
import p from './paragraphs';
import icon from './svgs';

const borderBottomDashed = {
  pb: [null, null, '3px'],
  borderBottom: [null, null, '1px dashed'],
  borderBottomColor: [null, null, 'lightMain1'],
};

const borderBottomMobile = {
  pb: '3px',
  borderBottom: '1px dashed',
  borderBottomColor: 'lightMain1',
};

const borderBottomClassic = {
  borderBottom: '1px solid',
  borderColor: 'second.1',
};

const birthdatesWrapper: Variant = {
  display: ['flex', 'flex'],
  width: [null, 'fit-content'],
  alignItems: 'center',
  flexDirection: ['column-reverse', 'row', 'row'],
};

const birthdatesWrapperForPersonalDataBlock: Variant = {
  display: ['flex', 'flex'],
  alignItems: 'center',
  flexDirection: 'inherit',
};

const avatar: Variant = {
  '& > img': {
    borderRadius: 3,
    height: [32, null, 100],
    width: [32, null, 100],
    minWidth: [32, null, 100],
  },
  '& >div': {
    mt: 2,
    '& p:nth-of-type(2n)': {
      textAlign: 'center',
      mt: 1,
    },
  },
};

const footnotesContainer: Variant = {
  '& p': {
    ...p.footnoteRegular,
    pb: 1,
  },
};
const paragraphsContainer: Variant = {
  '& p': {
    ...p.bodyRegular,
    pb: 1,
  },
};

const primaryContainerWithoutPadding: Variant = {
  bg: 'white',
  boxShadow: 'default',
  borderRadius: 2,
  color: 'main.2',
};

const primaryContainer: Variant = {
  ...primaryContainerWithoutPadding,
  px: [3, null, 5],
  py: [3, null, 6],
  mb: 6,
};

const primaryContainerPopover: Variant = {
  bg: 'white',
  boxShadow: [null, 'default'],
  borderRadius: [null, 2],
  height: 'fit-content',
  maxHeight: [null, `calc(100vh - 160px)`],
  overflowY: 'auto',
  p: 4,
};

const price: Variant = {
  display: 'inline-flex',
  flexDirection: 'column',
  span: {
    fontWeight: 1,
    lineHeight: 2,
    fontFamily: 'heading',
  },
};

const priceMedium: Variant = {
  ...price,
  '> div > div > span': {
    ':first-of-type': {
      fontSize: 5,
    },
    ':last-of-type': {
      fontSize: 1,
    },
  },
  '.discount': {
    borderRadius: 20,
    px: 1,
    py: '2px',
  },
  label: {
    mt: [1, null, 0],
    pb: '3px',
    borderBottom: '1px solid',
    borderBottomColor: 'lightMain1',
  },
};

const priceMediumWithoutBorder: Variant = {
  ...price,
  pt: 2,
  pb: 2,
  '> div > div > span': {
    ':first-of-type': {
      fontSize: 5,
    },
    ':last-of-type': {
      fontSize: 1,
    },
  },
  '.discount': {
    borderRadius: 20,
    px: 1,
    py: '2px',
  },
  label: {
    mt: [1, null, 0],
    pb: '3px',
  },
};

const priceBig: Variant = {
  ...price,
  '> div': {
    color: 'main.2',
    display: 'flex',
    flexDirection: ['row-reverse', null, 'column'],
    '> div': {
      textAlign: 'right',
      '> span': {
        ':first-of-type': {
          fontSize: [3, null, 6],
        },
        ':last-of-type': {
          fontSize: [0, null, 3],
        },
      },
    },
  },
  '.discount': {
    borderRadius: 2,
    ml: [null, null, 'auto'],
    mr: [1, null, 0],
    my: 'auto',
    p: 1,
  },
  label: {
    mt: 1,
    textAlign: 'right',
  },
};

const priceBigTopThree: Variant = {
  ...price,
  '> div': {
    color: 'main.2',
    display: 'flex',
    flexDirection: ['row-reverse', null, 'column'],
    '> div': {
      textAlign: 'right',
      '> span': {
        ':first-of-type': {
          fontSize: '40px',
        },
        ':last-of-type': {
          fontSize: '20px',
        },
      },
    },
  },
  '.discount': {
    borderRadius: 2,
    ml: [null, null, 'auto'],
    mr: [1, null, 0],
    my: 'auto',
    p: 1,
  },
  label: {
    mt: 1,
    textAlign: 'right',
  },
};

const priceBigWithBorder: Variant = {
  ...priceBig,
  '> div': {
    color: 'main.2',
    display: 'flex',
    flexDirection: ['row-reverse', null, 'column'],
    '> div': {
      width: 'min-content',
      maxHeight: 45,
      alignSelf: 'flex-end',
      ...borderBottomDashed,
      pb: 0,
      '> span': {
        ':first-of-type': {
          fontSize: [3, null, 6],
        },
        ':last-of-type': {
          fontSize: [0, null, 3],
        },
      },
    },
  },
  label: {
    mt: 3,
    textAlign: 'right',
  },
  cursor: 'pointer',
};

const priceCompare: Variant = {
  ...price,
  '> div': {
    color: 'main.2',
    display: 'flex',
    flexDirection: ['row-reverse', null, 'column'],
    '> div': {
      textAlign: 'right',
      '> span': {
        ':first-of-type': {
          fontSize: [3, null, 5],
        },
        ':last-of-type': {
          fontSize: [0, null, 1],
        },
      },
    },
  },
  '.discount': {
    borderRadius: 2,
    ml: [null, null, 'auto'],
    mr: [1, null, 0],
    my: 'auto',
    p: 1,
  },
  label: {
    textAlign: 'right',
    fontSize: [0, null, 1],
  },
};

const ratingReviews: Variant = {
  '> div': {
    display: 'flex',
    flexDirection: 'row',
    mb: 1,
    '> div': {
      my: 'auto',
    },
  },
};

const formGroup: Variant = {
  display: 'flex',
  flexDirection: 'column',
  color: 'text',
  'input, select': {
    color: 'text',
    boxSizing: 'border-box',
    fontWeight: 0,
  },
  input: {
    lineHeight: 2,
  },
  'input::placeholder, select::placeholder': {
    color: 'placeholder',
    fontWeight: 0,
    fontSize: 3,
  },
  '> img': {
    objectFit: 'contain',
  },
};

const formGroupError: Variant = {
  ...formGroup,
  color: 'error',
};

const formGroupInline: Variant = {
  color: 'text',
  display: 'flex',
  alignItems: 'center',
  // '& > label': {
  //   fontSize: 2,
  //   fontWeight: 500,
  //   mr: 2,
  // },
};

const formGroupInlineReversed: Variant = {
  ...formGroupInline,
  flexDirection: 'row-reverse',
  justifyContent: 'flex-end',
  // '& > label': {
  //   fontSize: 3,
  //   fontWeight: 0,
  //   ml: 2,
  // },
};

const formGroupInlineReversedWithBorder: Variant = {
  ...formGroupInlineReversed,
  my: 4,
  pt: 4,
  borderTop: '1px solid',
  borderColor: 'second.1',
};

const sustainability: Variant = {
  bg: 'second.2',
  px: 3,
  py: 2,
  svg: {
    mr: 3,
  },
};

const prosCons: Variant = {
  '> div': {
    div: {
      fontSize: 2,
      lineHeight: 2,
      color: 'main.2',
    },
    svg: {
      mt: '2px',
    },
  },
};

const rowBorder: Variant = {
  borderBottom: ['1px solid', null, 'none'],
  borderBottomColor: 'second.1',
};

const rowPadding: Variant = {
  py: [3, null, 0],
};

const top3ProductLogoWrapper = {
  maxWidth: [60, 80],
};

const product: Variant = {
  ...primaryContainerWithoutPadding,
  '> div': {
    '.alwaysVisible > div': {
      '.buildButton': {
        ml: 'auto',
        mt: [2, null, 3],
      },
      ...rowPadding,
      '&:nth-of-type(1)': {
        ...rowBorder,
        pt: 0,
      },
      '&:nth-of-type(2)': {
        ...rowBorder,
        borderLeftStyle: [null, null, 'solid'],
        borderLeftWidth: '1px',
        borderLeftColor: 'second.1',
        borderRightStyle: [null, null, 'solid'],
        borderRightWidth: '1px',
        borderRightColor: 'second.1',
        justifyContent: [null, null, 'space-between'],
        py: [null, null, 4],
        px: [null, null, 3],
        width: [null, null, '100%'],
        '> div': {
          my: [null, null, 'auto'],
          '&:nth-of-type(1)': {
            mr: [null, null, 6],
          },
          '&:nth-of-type(2)': {
            '> div': {
              '> div': {
                my: [null, null, 'auto'],
              },
            },
          },
        },
      },
    },
  },
  '&:not(.open)': {
    '> div': {
      '.alwaysVisible > div': {
        '&:nth-of-type(3)': {
          pb: 0,
        },
      },
    },
  },
  '&.open': {
    pb: [null, null, 35],
    '> div.expandedPart': {
      '> div.desktop': {
        mt: 6,
        '> div.separator': {
          bg: 'second.1',
          height: '1px',
          width: '100%',
          mb: 6,
        },
        '> div:nth-of-type(2)': {
          '> div': {
            '&:last-of-type': {
              '> div > div': {
                '&:last-of-type:not(:first-of-type)': {
                  ml: 0,
                },
              },
            },
          },
        },
      },
      '> div.mobile': {
        '> div:first-of-type': {
          borderBottom: ['1px solid', null, 'none'],
          borderBottomColor: 'second.1',
          borderTop: ['1px solid', null, 'none'],
          borderTopColor: 'second.1',
          ...rowPadding,
        },
      },
    },
  },
};

const topThreeProduct: Variant = {
  bg: 'white',
  boxShadow:
    '0px 16px 40px 0px rgba(0, 0, 0, 0.08), 0px 4px 24px 0px rgba(62, 72, 101, 0.08), 0px 4px 16px 0px rgba(78, 90, 126, 0.05)',
  borderRadius: '24px',
  '> div': {
    '.alwaysVisible': {
      display: 'grid',
      gridTemplateRows: '120px minmax(190px, 1fr) auto',
    },
    '.alwaysVisible > div': {
      '.buildButton': {
        ml: 'auto',
        mt: [2, null, 3],
      },
      '&:nth-of-type(1)': {
        pt: 0,
      },
      '&:nth-of-type(2)': {
        borderBottom: '1px solid',
        borderBottomColor: 'second.1',

        width: [null, null, '100%'],
        '> div': {
          my: [null, null, 'auto'],
          '&:nth-of-type(1)': {
            mr: [null, null, 6],
          },
          '&:nth-of-type(2)': {
            '> div': {
              '> div': {
                my: [null, null, 'auto'],
              },
            },
          },
        },
      },
    },
  },
  '&:not(.open)': {
    '> div': {
      '.alwaysVisible > div': {
        '&:nth-of-type(3)': {
          pb: 0,
        },
      },
    },
  },
  '&.open': {
    '.expandedPart': {
      '> div.desktop': {
        '> div:nth-of-type(2)': {
          '> div': {
            '&:last-of-type': {
              '> div > div': {
                '&:last-of-type:not(:first-of-type)': {
                  ml: 0,
                },
              },
            },
          },
        },
      },
    },
  },
};

const grayedContainer: Variant = {
  '> *:not(button:last-of-type)': {
    filter: 'grayscale(1)',
    opacity: 0.6,
  },
};

const grayedContainerMobile: Variant = {
  '> ul > li > div': {
    filter: 'grayscale(1)',
    opacity: 0.6,
  },
};

const unavailablePriceContainer: Variant = {
  mt: '4',
  textAlign: 'right',
  maxWidth: '120px',
};

const productTabSwitch: Variant = {
  'div.tab-section': {
    mt: [3, 0],
    '&:first-of-type > p': {
      mb: 3,
    },
    '&:last-of-type > p': {
      mb: 6,
    },
  },
};

const adviceCard = {
  bg: 'white',
  borderRadius: '16px',
  paddingTop: [2, 2, '22px'],
  paddingBottom: ['16px', '16px', '24px'],
  px: ['16px', '16px', '24px'],
  color: 'text',
  boxShadow:
    '0px 12px 28px rgba(71, 82, 100, 0.07), 0px 2px 8px rgba(78, 90, 126, 0.06), 0px 4px 40px rgba(62, 72, 101, 0.05)',
};

const baseRadioTile: Variant = {
  bg: 'white',
  borderRadius: 2,
  color: 'text',
  boxShadow:
    '0px 0px 2px rgba(78, 90, 126, 0.08), 0px 4px 12px rgba(62, 72, 101, 0.14)',
};

const radioTileHorizontal: Variant = {
  ...baseRadioTile,
  p: '18px 18px',
};

const radioTileVertical: Variant = {
  ...baseRadioTile,
  p: '8px 8px',
};

const labelChecked = {
  'p': { color: '#FFFFFF !important' },
};

const primaryCard: Variant = {
  bg: 'white',
  borderRadius: 2,
  padding: [3, 6],
  color: 'text',
  boxShadow: 'default',
};

const secondaryCard: Variant = {
  ...primaryCard,
  bg: 'main.2',
  color: 'white',
};

const complementaryCard: Variant = {
  ...primaryCard,
  bg: 'second.2',
};

const membershipUsp: Variant = {
  ...primaryCard,
  boxShadow: 'unset',
  padding: [4],
  bg: 'second.2',
  h4: {
    color: 'main.2',
  },
  '> div > p': {
    color: 'main.2',
    fontSize: 3,
  },
};

const negativeMarginStyle: Variant = {
  '& > div > p': {
    marginTop: '-20px',
    marginBottom: 6,
  },
};

const sections: Variant = {
  '> div': {
    py: [3, null, 4],
    '&:not(:last-of-type)': {
      borderBottom: '1px solid',
      borderColor: 'second.1',
    },
  },
};

const checkboxGroup: Variant = {
  '> div > label': {
    fontWeight: 1,
  },
  '> div': {
    mt: 3,
    '> div > label': {
      fontSize: 3,
      fontWeight: 0,
      mb: 0,
      ml: 2,
    },
  },
};

const select = {
  '> label': {
    fontSize: 2,
    fontWeight: 1,
  },
  '> div': {
    mt: 4,
  },
};

const filters = {
  '> div': {
    '&:not(:last-of-type)': {
      borderBottomColor: 'second.1',
      borderBottomStyle: 'solid',
      borderBottomWidth: '1px',
      pb: 4,
    },
    mt: 4,
    '> div > h4': {
      color: 'main.2',
      fontFamily: 'heading',
      fontSize: 4,
    },
  },
};

const bannerInfo: Variant = {
  bg: 'main.2',
  borderRadius: '16px',
  p: [3, 4],
  '.banner-title': {
    ...p.bodyImportantLight,
    mb: 1,
  },
  ...p.bodyLight,
  '& > svg': {
    ...icon.regularLight,
    mb: [2, 0],
    mr: [0, 4],
  },
};

const bannerWarning: Variant = {
  ...bannerInfo,
  bg: 'warning',
  color: 'text',
  '.banner-title': {
    ...p.bodyImportant,
    mb: 1,
  },
  '& >svg': {
    ...icon.regular,
  },
};

const bannerSuggestion: Variant = {
  ...bannerWarning,
  bg: 'second.2',
};

const startWidget: Variant = {
  ...primaryContainer,
  boxSizing: 'border-box',
  px: 6,
  py: 6,
  fontFamily: 'heading',
  '& > p': {
    fontSize: '16px',
    lineHeight: '24px',
    fontFamily: 'Segoe UI, sans-serif',
    mb: 30,
  },
  '& a': {
    textDecoration: 'underline',
    color: 'info',
  },
  width: [null, null, 410],
  '& > h3': {
    fontSize: 6,
    mt: 0,
    mb: 6,
  },
  '& button': {
    mt: 6,
    width: '100%',
  },
};

const startWidgetHorizontal: Variant = {
  ...startWidget,
  justifyContent: 'flex-start!important',
  boxSizing: 'border-box',
  width: ['375px', '375px', '780px'],
  mb: null,
  '& p + div > div > div > div': {
    minWidth: ['88px', null, null],
    maxWidth: [null, '159px', '159px'],
  },
  '& button': {
    mb: 'auto',
    minWidth: '190px',
    width: ['100%', '100%'],
    height: 'inherit',
    lineHeight: '23px',
  },
  '& > div': {
    alignItems: 'baseline',
    flexDirection: ['column', 'column', 'row'],
  },
};

const startWidgetHorizontalPadded: Variant = {
  ...startWidgetHorizontal,
  width: '768px',
  minHeight: [null, '372px', '372px'],
  justifyContent: 'space-around!important',
};

const modalContainer: Variant = {
  bg: 'white',
  borderRadius: 2,
  boxShadow: 'default',
  my: 7,
  width: ['100%', '80%', '60%'],
  maxWidth: 760,
  height: 'fit-content',
  maxHeight: [null, 'calc(100% - 160px)'],
  zIndex: '90',
};

const productModalTopPanel: Variant = {
  borderTopRightRadius: 2,
  borderTopLeftRadius: 2,
  bg: 'white',
  px: 6,
  pb: 3,
  justifyContent: 'space-between',
};

const productModalProductPrice: Variant = {
  ...priceBig,
};

const YPaddingMedium = {
  py: 6,
};

/** TAB VIEW **/
const lightTabViewTabsContainer: Variant = {
  pt: 2,
  pb: 0,
  fontSize: '1.1rem',
  flexDirection: ['column', null, 'row'],
  '> div:nth-child(2n)': {
    display: ['none', null, 'block'],
  },
};

const lightTabViewTabsContainerForModal: Variant = {
  ...lightTabViewTabsContainer,
  pt: [0, null, 6],
  pb: [0, null, 3],
  overflow: 'auto',
  '> div:nth-child(1n)': {
    marginBottom: [3, null],
    whiteSpace: 'nowrap',
  },
  '> div:nth-child(2n)': {
    display: ['none', null, 'block'],
    minWidth: 10,
    width: 50,
    '> div': {
      width: '100%',
    },
  },
};

const lightTabViewTab: Variant = {
  cursor: 'pointer',
  color: 'second.0',
};

const lightTabViewTabSelected: Variant = {
  ...lightTabViewTab,
  color: 'text',
};

const tabViewTabsContainer: Variant = {
  display: 'flex',
  overflowX: ['scroll', null, 'visible'],
  mb: [3, 6],
  '& > div': {
    fontWeight: 1,
    color: '#2B2F3B !important',
    borderBottomColor: '#12DE9A',
  },
};

const tabViewTabsContainerIndented: Variant = {
  ...tabViewTabsContainer,
  borderBottom: '1px solid',
  borderColor: 'second.1',
  px: 6,
  whiteSpace: 'nowrap',
};

const tabViewTab: Variant = {
  color: 'main.1',
  mr: 5,
  py: 2,
};

const tabViewTabSelected: Variant = {
  ...tabViewTab,
  borderBottom: '3px solid',
  borderColor: 'main.1',
  fontWeight: 1,
};

const tabViewContent: Variant = {};

const tabViewContentIndented: Variant = {
  px: 6,
  pb: 3,
};

const divider: Variant = {
  borderTop: '1px solid',
  borderColor: 'divider',
  marginBottom: 2,
};

const detailsListHeading: Variant = {
  pb: 3,
};

const unitInput = {
  ...p.bodyRegular,
  color: 'second.0',
};

const operational = {
  width: '7px',
  height: '7px',
  borderRadius: 3,
  mr: 2,
  '&.active': {
    backgroundColor: 'supplementary.2',
  },
  '&.offline': {
    backgroundColor: 'supplementary.5',
  },
};

const contactItem = {
  mb: [4, 6],
  alignItems: 'center',
  ':last-of-type': {
    mb: 0,
  },
};

const accordionContainer: Variant = {
  ul: {
    '&:not(:last-of-type)': {
      borderBottomColor: 'second.1',
      borderBottomStyle: 'solid',
      borderBottomWidth: '1px',
    },
  },
};

const modalAccordionContent = {
  ...p.bodyRegular,

  p: {
    ...p.bodyRegular,
    m: 0,
    pt: 6,
    'first-of-type': {
      pt: 0,
    },

    '> a': {
      ...anchors.anchorRegular,
    },
  },
  ul: {
    pb: 6,
    pl: 2,
    pt: 2,
    listStyle: 'inside',
    ':last-of-type': {
      pb: 0,
    },
  },
  ol: {
    pb: 6,
    'last-of-type': {
      pb: 0,
    },
  },
  table: {
    tableLayout: 'fixed',
    width: '100%',
    mt: 3,
  },
  'table, th, td': {
    borderCollapse: 'collapse',
    borderSpacing: 0,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'second.1',
    textAlign: 'center',
  },
};

const modalHeader = {
  p: {
    ...p.bodyRegular,
    '&:not(:last-of-type)': {
      pb: 6,
    },
  },
};

const uspParagraphContainer = {
  p: {
    ...p.footnoteLight,
  },
  '&:not(:last-of-type)': {
    mb: 4,
  },
};

const uspParagraphContainerTop = {
  h4: {
    color: 'second.4',
  },
  p: {
    ...p.bodyRegular,
    color: 'second.4',
  },
  '&:not(:last-of-type)': {
    mb: 4,
  },
  alignItems: 'start',
};

const borderCard: Variant = {
  bg: 'white',
  borderColor: 'second.1',
  borderStyle: 'solid',
  borderWidth: '1px',
  borderRadius: 2,
  padding: 3,
  color: 'text',
};

const personalDataFieldContainer = {
  '&:not(:first-of-type)': {
    mt: 3,
  },
  '> div': {
    m: 0,
  },
};

const personalDataTemplateContainer = {
  '&:not(:first-of-type)': {
    mt: 3,
  },
};

const truncatedReview = {
  fontSize: 2,
  lineHeight: 2,
};

const addressInSidenav = {
  '> div > div:nth-child(1)': {
    minWidth: '96px',
  },
};

const sidenav = {
  minWidth: '300px',
};

const prosConsItem = {
  ...borderBottomDashed,
};

const afterTheSwitchApply = {
  ...primaryCard,
  '> p:first-child': {
    fontSize: [2, 4],
  },
};

const promotionBar = {
  bg: '#D6E6FF',
  borderTopLeftRadius: 2,
  borderTopRightRadius: 2,
  px: [3, null, 5],
  py: '4px',
  svg: {
    mr: 2,
  },
  '> div:not(:last-child)': {
    mb: [2, null, 0],
  },
};

const promotionBarItem = {
  '> p': {
    ...borderBottomMobile,
  },
};
const applyFormFooterContainer = {
  mt: 6,
  ...p.captionRegular,
  color: 'second.0',
  'p:not(:first-child)': {
    mt: 2,
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
};

const circleWrapper = {
  borderRadius: '50%',
  p: 3,
  fontSize: 0,
  display: 'inline-flex',
};

const productUsp: Variant = { color: 'main.2', cursor: 'pointer', fontSize: 2 };

const CheckoutTopNotification = {
  ...primaryCard,
  py: [4, null, 7, 7],
  px: [3, null, 7, 7],
  '> p:not(:last-child)': {
    marginBottom: 6,
  },
};

const termsListWrapper = {
  '> p': {
    color: 'main.1',
    svg: {
      marginLeft: 2,
    },
  },
};

const tooltipContainer = {
  ...primaryContainer,
  width: '200px',
};

const innerComparisonContainer = {
  display: 'flex',
  width: '100%',
};

const sustainabilityRegular: Variant = {
  svg: {
    mr: 2,
    width: '20px',
    height: '20px',
  },
  span: {
    ...p.footnoteRegular,
  },
};

const outerComparisonContainer: Variant = {
  borderBottom: '1px solid',
  borderColor: 'second.1',
};

const headRowContainer: Variant = {
  columnGap: '1.875rem',
  verticalAlign: 'top',
  overflowY: 'hidden',
  display: 'flex',
  '>div': {
    maxWidth: '275px',
    minWidth: '150px',
    paddingTop: 3,
    paddingBottom: 3,
    flex: 1,
    display: 'flex',
  },
  '> div: not(:first-child)': {
    background: 'white',
    boxShadow: 'default',
    padding: '15px 15px 1px 15px',
  },
};

const bodyRowContainer: Variant = {
  ...headRowContainer,
  paddingRight: 3,
  paddingBottom: 0,
  ':last-of-type': {
    'div: not(:first-child)': {
      '>div': {
        paddingBottom: 3,
        borderBottom: '1px solid',
        borderColor: 'second.1',
        alignItems: 'stretch',
        display: 'flex',
        width: '100%',
      },
    },
  },
};

const cellContainer: Variant = {
  display: 'flex',
  '>div': {
    width: '100%',
    paddingBottom: 3,
    borderBottom: '1px solid',
    borderColor: 'second.1',
  },
};

const accordionChild: Variant = {
  '>div': {
    paddingLeft: 4,
  },
};

const accordionHeader: Variant = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  cursor: 'pointer',
};

const uniqueRowField: Variant = {
  width: [null, null, '50%'],
};

const addressDisplay: Variant = {
  ...outerComparisonContainer,
  mb: 4,
  pb: 3,
};

const turnPhoneContainer: Variant = {
  display: 'flex',
  backgroundColor: 'main.1',
  padding: 3,
  borderRadius: 3,
  justifyContent: 'center',
  alignItems: 'center',
};

const mobileExpContainer: Variant = {
  height: `calc(100vh - 170px)`,
  display: 'flex',
  justifyContent: 'center',
};

const halfWidth: Variant = {
  ...formGroup,
  boxSizing: 'border-box',
  pr: [null, null, 3],
  width: [null, null, '50%'],
};

const filterLabel: Variant = {
  '> div > label': {
    color: 'heading',
    fontSize: 3,
    fontWeight: 1,
    mb: 0,
    pb: 0,
  },
};

const packageLabel: Variant = {
  '> div > label': {
    color: 'heading',
    fontSize: [3, 3, 2],
    fontWeight: [1, 1, 0],
    mb: 0,
    pb: 0,
  },
};

const contractLabel: Variant = {
  '> div > label': {
    color: 'heading',
    fontSize: 2,
    fontWeight: 1,
    mb: 0,
    pb: 0,
  },
};

const addressBirthdateDisplay: Variant = {
  '> div': {
    '> div': {
      mt: 0,
      '> div': {
        p: 0,
      },
    },
    '> h5': {
      mt: 0,
    },
  },
};

const addressBirthdateDisplayWithBorder: Variant = {
  borderBottom: '1px solid',
  borderTop: '1px solid',
  borderColor: 'second.1',
  py: 6,
  ...addressBirthdateDisplay,
};

const helpContact: Variant = {
  ...anchors.helpContact,
};

const modalCompareContainer: Variant = {
  bg: 'rgba(0,0,0,0)',
  position: 'fixed',
  height: '100%',
  right: 0,
  left: 0,
  pointerEvents: 'none',
};

const modalCompare: Variant = {
  alignItems: 'center',
  bg: 'rgba(255,255,255,0.95)',
  position: 'fixed',
  mb: ['0px', '0px', '20px'],
  width: ['100%', '100%', '85%'],
  maxWidth: ['100%', '100%', '1029px'],
  padding: [0, 6, 6],
  boxShadow: '0px 0px 20px rgba(0, 0, 0, 0.15)',
  pointerEvents: 'all',
  borderRadius: '10px',
  bottom: 0,
  zIndex: 1,
};

const compareEmptyProduct: Variant = {
  borderStyle: 'dashed',
  borderColor: 'second.1',
  flexGrow: 1,
  flexShrink: 0,
  flexBasis: 0,
  borderRadius: '10px',
  mr: '30px',
  mb: '10px',
  mt: '20px',
  ml: '10px',
};

const compareEmptyProductStickyHeader: Variant = {
  width: '100%',
  padding: '8px',
  border: '2px dashed #BEC0C3',
  borderRadius: '8px',
  cursor: 'pointer',
  boxSizing: 'border-box',
};

const compareProduct: Variant = {
  flexGrow: 1,
  flexShrink: 0,
  flexBasis: 0,
  borderRadius: '10px',
  ml: '10px',
  mr: '30px',
  mb: '10px',
  pb: '10px',
  mt: '20px',
  boxShadow: '0px 0px 20px rgba(0, 0, 0, 0.15)',
};

const productWrapperCompare: Variant = {
  display: 'flex',
  flexDirection: ['column', 'column', 'row'],
  flexWrap: ['nowrap', 'nowrap', 'wrap'],
  overflowY: 'auto',
  width: '100%',
};

const percentageBar: Variant = {
  pb: 3,
  mb: 3,
  ...borderBottomClassic,
  '> div': {
    height: 2,
    backgroundColor: 'second.1',
    width: '100%',
    borderRadius: '28px',
  },
};

const rangeSlider: Variant = {
  position: 'relative',
  maxWidth: '240px',
  '> div': {
    '&:first-of-type': {
      width: '100%',
      backgroundColor: 'second.1',
      zIndex: 1,
    },
    position: 'absolute',
    height: '4px',
    borderRadius: '8px',
    backgroundColor: 'main.1',
    zIndex: 2,
  },
};

const moreInfoReview: Variant = {
  ...borderBottomClassic,
  mb: 6,
  pb: 6,
};

const searchModal: Variant = {
  position: 'absolute',
  zIndex: 1,
  backgroundColor: 'main.0',
  width: '350px',
  marginTop: 1,
  overflowY: 'scroll',
  boxShadow: 'default',
  maxHeight: ['190px', '190px', '480px'],
  borderRadius: 2,
};

const fullWidthSearchModal: Variant = {
  ...searchModal,
  position: 'absolute',
  width: '100%',
};

const productRightCol: Variant = {
  '>div': {
    '&:first-of-type>div:first-of-type': {
      mr: [60, null, 4],
    },
    '&:last-of-type': {
      display: ['flex', null, 'none'],
      flexDirection: 'column',
      '>div:first-of-type': {
        mt: 3,
      },
    },
  },
};

const mobileResetFiltersWrapper: Variant = {
  display: 'flex',
  justifyContent: 'end',
};

const countdown: Variant = {
  display: 'inline-block',
  pr: 2,
  py: 1,
  pl: 6,
  borderTopRightRadius: 3,
  borderBottomRightRadius: 3,
};

const countdownReverted: Variant = {
  display: 'inline-block',
  py: 1,
  px: 2,
  borderTopLeftRadius: 3,
  borderBottomLeftRadius: 3,
};

const iconMarkerBig: Variant = {
  position: 'relative',
  alignItems: 'center',
  bg: 'second.2',
  border: '1px solid main.2',
  borderRadius: 3,
  display: 'flex',
  height: 48,
  justifyContent: 'center',
  width: 48,
  '> svg': {
    width: 24,
    height: 24,
  },
  zIndex: 1,
};

const iconMarkerSmall: Variant = {
  ...iconMarkerBig,
  width: 40,
  height: 40,
  '> svg': {
    width: 21,
    height: 21,
  },
};
const countbar: Variant = {
  display: 'flex',
  '> div': {
    display: 'flex',
    boxShadow: 'default',
    justifyContent: 'center',
    alignItems: 'center',
    width: 20,
    height: 25,
    fontWeight: 1,
    fontSize: 2,
    fontFamily: 'heading',
    borderRadius: 4,
    ':not(:last-child)': {
      marginRight: '2px',
    },
  },
};

const fullWidthOfScreen: Variant = {
  width: '100vw',
  position: 'relative',
  left: '50%',
  right: '50%',
  marginLeft: '-50vw',
  marginRight: '-50vw',
  boxSizing: 'border-box',
};

const experienceCollectiveSliderContainer: Variant = {
  ...fullWidthOfScreen,
  gap: 14,
  '& > :first-child': {
    marginLeft: 4,
  },
  '& > :last-child': {
    marginRight: 4,
  },
  scrollbarWidth: 'none',
  scrollSnapType: 'x mandatory',
  scrollPadding: 16,
};

const filterOptionsContainer: Variant = {
  ...primaryContainerWithoutPadding,
  position: ' absolute',
  top: '85px',
  minWidth: '350px',
  left: '15px',
};

const filterOptionItem = {
  cursor: 'pointer',
  padding: 3,
  '&:hover': {
    background: 'second.1',
  },
};

const deductibleAmountContainer: Variant = {
  display: 'flex',

  gap: [3, null, '8px'],
  flexDirection: ['column', null, 'row'],
  textAlign: 'center',
};

const deductibleAmountButton: Variant = {
  flex: '1',
  p: 3,
  cursor: 'pointer',
  color: 'main.2',
  borderRadius: '8px',
};

const priceMediumProductModal: Variant = {
  ...priceMediumWithoutBorder,
  pt: 0,
  pb: 0,
  mr: 3,
  '> div > div > span': {
    ':first-of-type': {
      color: 'main.2',
      fontSize: 4,
    },
    ':last-of-type': {
      color: 'main.2',
      fontSize: 1,
    },
  },
  label: {
    mt: [1, null, 0],
  },
};

const affiliateUrlPage: Variant = {
  bg: 'second.1',
  position: 'fixed',
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
  display: 'grid',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'auto',
};

const affiliateUrlPageModal: Variant = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gridGap: '16px',
  maxWidth: 800,
  m: [4, 4, 0, 0],
  p: '32px',
  boxShadow:
    '0px 24px 56px rgba(78, 98, 126, 0.1), 0px 0px 48px rgba(78, 98, 126, 0.1), 0px 16px 32px rgba(78, 98, 126, 0.05)',
  borderRadius: '24px',
  bg: 'white',

  h2: {
    fontSize: ['24px', '24px', '32px', '32px'],
  },

  '> p': {
    color: '#9B9B9B',
  },
};

const extraServiceAccordionSubHeader: Variant = {
  '& > ul > li > div': {
    position: 'relative',

    '& > div > span': {
      position: 'absolute',
      left: 0,
      top: '24px',

      '& p': {
        display: 'none',
      },
    },
  },
};

const sliderWrapper: Variant = {
  display: 'flex',
  overflowX: 'auto',
  padding: [0, 0, '16px 20px'],

  '&::-webkit-scrollbar': {
    height: '5px',
    backgroundColor: '#d4d7dd',
  },

  '&::-webkit-scrollbar-track': {
    boxShadow: '#f1f1f1',
  },

  '&::-webkit-scrollbar-thumb': {
    background: '#888',
  },

  '&::-webkit-scrollbar-thumb:hover': {
    background: '#555',
  },
};

const tabContainerTopThree: Variant = {
  display: 'flex',
  width: '100%',
  padding: '4px 8px',
  justifyContent: 'space-between',
  borderTop: '1px solid',
  borderColor: '#F0F0EF',
  background: '#F8F8F7',
  whiteSpace: 'nowrap',
  overflowX: 'auto',
  fontFamily: 'body',
  '&::-webkit-scrollbar': {
    height: '5px',
    backgroundColor: '#d4d7dd',
  },

  '&::-webkit-scrollbar-track': {
    boxShadow: '#f1f1f1',
  },

  '&::-webkit-scrollbar-thumb': {
    background: '#888',
  },

  '&::-webkit-scrollbar-thumb:hover': {
    background: '#555',
  },
};

const topThreeDesktopWrapper: Variant = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gridGap: '32px',
  justifyContent: 'center',
};

const topThreeMobileWrapper: Variant = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gridGap: '16px',
};

const topThreeOuterWrapper: Variant = {
  '@media (min-width: 767px)': {
    pb: '24px',
    boxShadow:
      '0px 4px 8px 0px rgba(62, 72, 101, 0.08), 0px 2px 8px 0px rgba(78, 90, 126, 0.08)',
    borderBottomLeftRadius: '40px',
    borderBottomRightRadius: '40px',
    background:
      'linear-gradient(0deg, #F8F8F7 0%, #F8F8F7 100%), linear-gradient(0deg, rgba(255, 255, 255, 0.80) 0%, rgba(255, 255, 255, 0.80) 100%), linear-gradient(333deg, rgba(210, 255, 241, 0.60) 14.22%, #EBFFF1 91.57%)',
  },
};

const filterSorting: Variant = {
  display: 'flex',
  padding: '4px 8px',
  alignItems: 'center',
  gap: '8px',
  '>span': {
    ...p.bodyRegular,
    color: 'main.1',
  },
};

const bannerBackgroundContainer: Variant = {
  display: 'grid',
  backgroundImage: 'radial-gradient(circle, #0cbb8a 0, #097d56 70%)',
  backgroundPosition: 'bottom, 50%',
  backgroundRepeat: 'no-repeat',
  gridTemplateColumns: [
    'unset',
    'unset',
    'repeat(12, minmax(0, 1fr))',
    'repeat(12, minmax(0, 1fr))',
  ],
  position: 'relative',
  overflow: 'hidden',
  alignContent: ['unset', 'unset', 'center', 'center'],
  py: [24, 24, 40, 40],
  px: [16, 16, 0, 0],
  justifyItems: 'center',
  '&::before': {
    display: ['none', 'none', 'initial', 'initial'],
    content: '""',
    position: 'absolute',
    left: '0',
    bottom: '0',
    width: '407px',
    height: '524px',
    backgroundImage:
      "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='407' height='524' fill='none'%3E%3Cpath d='M-195.355 254.436C-82.37 230.287 32.875 282.068 91.403 383.44c58.497 101.321 45.799 227.039-31.658 312.842l-3.346 3.711 116.758 202.231 6.389-5.333c183.326-153.293 228.523-416.778 107.496-626.403C165.985 60.811-84.738-31.687-309.217 50.328l-7.731 2.89 116.728 202.178 4.865-.96z' fill='%23018A62' filter='url(%23a)'/%3E%3Cdefs%3E%3Cfilter id='a' x='-368.948' y='.63' width='775.502' height='985.594' filterUnits='userSpaceOnUse' color-interpolation-filters='sRGB'%3E%3CfeFlood flood-opacity='0' result='BackgroundImageFix'/%3E%3CfeColorMatrix in='SourceAlpha' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0' result='hardAlpha'/%3E%3CfeMorphology radius='12' in='SourceAlpha' result='effect1_dropShadow_9077_36348'/%3E%3CfeOffset dy='32'/%3E%3CfeGaussianBlur stdDeviation='32'/%3E%3CfeColorMatrix values='0 0 0 0 0.0625882 0 0 0 0 0.0926346 0 0 0 0 0.15702 0 0 0 0.2 0'/%3E%3CfeBlend in2='BackgroundImageFix' result='effect1_dropShadow_9077_36348'/%3E%3CfeBlend in='SourceGraphic' in2='effect1_dropShadow_9077_36348' result='shape'/%3E%3C/filter%3E%3C/defs%3E%3C/svg%3E\")",
    backgroundRepeat: 'no-repeat',
    pointerEvents: 'none',
  },
};
const bannerBackgroundContainerAfter: Variant = {
  width: '100%',

  '&::after': {
    display: ['none', 'none', 'initial', 'initial'],
    content: '""',
    position: 'absolute',
    top: '0',
    right: '0',
    width: '231px',
    height: '333px',
    backgroundImage:
      "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='231' height='333' fill='none'%3E%3Cpath d='M437.384 119.354c-73.046 32.276-158.331 13.492-212.231-46.847-53.873-60.309-63.012-147.167-22.702-216.151l1.74-2.983L96.664-267l-3.571 4.509C-9.335-132.907-2.756 51.756 108.704 176.53 220.19 301.335 402.89 328.562 543.217 241.405l4.824-3.045-107.5-120.342-3.157 1.336z' fill='%23039268' filter='url(%23a)'/%3E%3Cdefs%3E%3Cfilter id='a' x='.475' y='-267' width='567.566' height='599.184' filterUnits='userSpaceOnUse' color-interpolation-filters='sRGB'%3E%3CfeFlood flood-opacity='0' result='BackgroundImageFix'/%3E%3CfeColorMatrix in='SourceAlpha' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0' result='hardAlpha'/%3E%3CfeMorphology radius='4' in='SourceAlpha' result='effect1_dropShadow_9077_36347'/%3E%3CfeOffset dy='8'/%3E%3CfeGaussianBlur stdDeviation='4'/%3E%3CfeColorMatrix values='0 0 0 0 0.0627451 0 0 0 0 0.0941176 0 0 0 0 0.156863 0 0 0 0.04 0'/%3E%3CfeBlend in2='BackgroundImageFix' result='effect1_dropShadow_9077_36347'/%3E%3CfeColorMatrix in='SourceAlpha' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0' result='hardAlpha'/%3E%3CfeMorphology radius='4' in='SourceAlpha' result='effect2_dropShadow_9077_36347'/%3E%3CfeOffset dy='20'/%3E%3CfeGaussianBlur stdDeviation='12'/%3E%3CfeColorMatrix values='0 0 0 0 0.0627451 0 0 0 0 0.0941176 0 0 0 0 0.156863 0 0 0 0.1 0'/%3E%3CfeBlend in2='effect1_dropShadow_9077_36347' result='effect2_dropShadow_9077_36347'/%3E%3CfeBlend in='SourceGraphic' in2='effect2_dropShadow_9077_36347' result='shape'/%3E%3C/filter%3E%3C/defs%3E%3C/svg%3E\")",
    backgroundRepeat: 'no-repeat',
    pointerEvents: 'none',
  },
};
const bannerBackgroundTrustPilotSection: Variant = {
  flex: 1,
  zIndex: 2,
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'flex-end',
  height: '100%',
  width: '100%',
};

const divVariants = {
  productRightCol,
  moreInfoReview,
  productUsp,
  prosConsItem,
  accordionContainer,
  avatar,
  birthdatesWrapper,
  birthdatesWrapperForPersonalDataBlock,
  paragraphsContainer,
  priceBig,
  priceBigWithBorder,
  priceMedium,
  priceMediumWithoutBorder,
  priceCompare,
  primaryContainer,
  primaryContainerPopover,
  ratingReviews,
  footnotesContainer,
  formGroup,
  formGroupError,
  formGroupInline,
  formGroupInlineReversed,
  formGroupInlineReversedWithBorder,
  sustainability,
  prosCons,
  product,
  grayedContainer,
  grayedContainerMobile,
  productTabSwitch,
  primaryCard,
  secondaryCard,
  complementaryCard,
  filters,
  bannerInfo,
  bannerWarning,
  bannerSuggestion,
  sections,
  startWidget,
  startWidgetHorizontal,
  startWidgetHorizontalPadded,
  modalContainer,
  productModalTopPanel,
  productModalProductPrice,
  tabViewTabsContainer,
  tabViewTabsContainerIndented,
  tabViewTab,
  tabViewTabSelected,
  unitInput,
  tabViewContent,
  tabViewContentIndented,
  checkboxGroup,
  select,
  operational,
  contactItem,
  top3ProductLogoWrapper,
  modalAccordionContent,
  modalHeader,
  uspParagraphContainer,
  uspParagraphContainerTop,
  borderCard,
  personalDataFieldContainer,
  personalDataTemplateContainer,
  truncatedReview,
  addressInSidenav,
  sidenav,
  promotionBar,
  promotionBarItem,
  applyFormFooterContainer,
  membershipUsp,
  negativeMarginStyle,
  borderBottomClassic,
  YPaddingMedium,
  lightTabViewTabsContainer,
  lightTabViewTabsContainerForModal,
  lightTabViewTab,
  lightTabViewTabSelected,
  afterTheSwitchApply,
  circleWrapper,
  CheckoutTopNotification,
  termsListWrapper,
  tooltipContainer,
  divider,
  detailsListHeading,
  unavailablePriceContainer,
  innerComparisonContainer,
  sustainabilityRegular,
  outerComparisonContainer,
  uniqueRowField,
  addressDisplay,
  headRowContainer,
  bodyRowContainer,
  cellContainer,
  accordionChild,
  accordionHeader,
  turnPhoneContainer,
  mobileExpContainer,
  halfWidth,
  filterLabel,
  addressBirthdateDisplay,
  addressBirthdateDisplayWithBorder,
  helpContact,
  modalCompareContainer,
  modalCompare,
  compareEmptyProduct,
  compareProduct,
  productWrapperCompare,
  percentageBar,
  rangeSlider,
  searchModal,
  mobileResetFiltersWrapper,
  contractLabel,
  packageLabel,
  countdown,
  countdownReverted,
  iconMarkerBig,
  iconMarkerSmall,
  countbar,
  fullWidthOfScreen,
  experienceCollectiveSliderContainer,
  filterOptionsContainer,
  filterOptionItem,
  fullWidthSearchModal,
  deductibleAmountContainer,
  deductibleAmountButton,
  adviceCard,
  priceMediumProductModal,
  affiliateUrlPage,
  affiliateUrlPageModal,
  extraServiceAccordionSubHeader,
  topThreeProduct,
  sliderWrapper,
  tabContainerTopThree,
  topThreeMobileWrapper,
  topThreeOuterWrapper,
  topThreeDesktopWrapper,
  priceBigTopThree,
  radioTileHorizontal,
  radioTileVertical,
  labelChecked,
  filterSorting,
  compareEmptyProductStickyHeader,
  bannerBackgroundContainer,
  bannerBackgroundContainerAfter,
  bannerBackgroundTrustPilotSection,
};

export default divVariants;
